import { MENU_TYPE } from '@/services/auth/consts'
import { MENU_CODE_PATTERN, MENU_MAX_LENGTH, MENU_ORDER_PATTERN, MENU_TREE_ROOT, MESSAGES } from './consts'
import type { MenuRow } from './queries/useGetMenuList'

type MenuCells = Pick<MenuRow, 'MENU_CD' | 'MENU_NM' | 'MENU_TYP' | 'MENU_TREE' | 'MENU_ORD'> & { _id?: number }

/** 서버에 저장된 메뉴로 만든 기준 집합 */
export type MenuIndex = {
  /** 상위메뉴로 고를 수 있는 값 — root + root 아래 FOLD 메뉴코드 */
  parentCodes: ReadonlySet<string>
  /** 하위 메뉴가 있는 메뉴코드 */
  folderCodesWithChildren: ReadonlySet<string>
  /** 저장된 전체 메뉴코드(대문자) */
  savedCodes: ReadonlySet<string>
}

const MENU_TYPES: readonly string[] = Object.values(MENU_TYPE)
const empty = (v: unknown) => typeof v !== 'string' || !v.trim()
const LENGTH_KEYS = Object.keys(MENU_MAX_LENGTH) as (keyof typeof MENU_MAX_LENGTH)[]

/**
 * 저장 전 검증 — 걸리면 안내 문구, 통과면 undefined.
 * 표 셀렉트는 붙여넣기로 선택지 밖 값이 들어올 수 있어 저장 때 다시 막는다.
 * 2.0 사이드바는 root → FOLD → PROG 2단만 그려 BE 도 그 밖의 트리를 400 으로 거부한다(LOGISPRD-16595 two-level-tree-only) —
 *  FOLD 의 상위메뉴는 root, PROG 의 상위메뉴는 root 아래 FOLD(parentCodes 의 root 외 값)이고 자기 자신은 안 된다. FOLD 가 root 에만 붙으니 순환도 생기지 않는다.
 * 새 행의 메뉴코드는 형식(MENU_CODE_PATTERN)을 보고, 저장된 코드나 같은 묶음의 다른 새 행과 겹치면 막는다 — BE 400 으로 순차 저장이 중간에 멈추지 않게.
 *  기존 행은 PUT 이 메뉴코드를 보내지 않아 형식을 보지 않는다.
 * 하위 메뉴가 있는 폴더(folderCodesWithChildren)는 메뉴종류를 FOLD 에서 바꿀 수 없다 — 1.0 메뉴 트리가 PROG 의 하위를 펼치지 않는다.
 */
export const invalidReason = (rows: MenuCells[], { parentCodes, folderCodesWithChildren, savedCodes }: MenuIndex): string | undefined => {
  if (rows.some((r) => empty(r.MENU_CD) || empty(r.MENU_NM) || empty(r.MENU_TYP) || empty(r.MENU_TREE) || empty(r.MENU_ORD)))
    return MESSAGES.requiredCells
  // 메뉴코드 길이는 새 행만 본다 — PUT 은 메뉴코드를 보내지 않고, 기존 행은 MOMS_MENU_INFO(20자) 기준으로 이미 저장돼 있다
  const tooLong = (r: MenuCells, k: (typeof LENGTH_KEYS)[number]) => (k !== 'MENU_CD' || r._id == null) && r[k].trim().length > MENU_MAX_LENGTH[k]
  if (rows.some((r) => LENGTH_KEYS.some((k) => tooLong(r, k)))) return MESSAGES.tooLong
  const newCodes = rows.filter((r) => r._id == null).map((r) => r.MENU_CD.trim())
  if (newCodes.some((code) => !MENU_CODE_PATTERN.test(code))) return MESSAGES.invalidCode
  if (newCodes.some((code, i) => savedCodes.has(code) || newCodes.indexOf(code) !== i)) return MESSAGES.duplicateCode
  if (rows.some((r) => !MENU_TYPES.includes(r.MENU_TYP))) return MESSAGES.invalidType
  // 같은 묶음에서 PROG 로 바뀌는 폴더는 부모가 될 수 없다 — BE 는 행마다 그 시점 DB 를 보므로 앞 행 저장이 뒤 행 판정을 바꾼다
  const notFolders = new Set(rows.filter((r) => r.MENU_TYP !== MENU_TYPE.FOLD).map((r) => r.MENU_CD.trim()))
  const treeOk = (r: MenuCells) =>
    r.MENU_TYP === MENU_TYPE.FOLD
      ? r.MENU_TREE === MENU_TREE_ROOT
      : r.MENU_TREE !== MENU_TREE_ROOT && parentCodes.has(r.MENU_TREE) && !notFolders.has(r.MENU_TREE)
  if (!rows.every(treeOk)) return MESSAGES.invalidTree
  if (rows.some((r) => r.MENU_TYP !== MENU_TYPE.FOLD && folderCodesWithChildren.has(r.MENU_CD.trim()))) return MESSAGES.folderHasChildren
  if (rows.some((r) => !MENU_ORDER_PATTERN.test(r.MENU_ORD))) return MESSAGES.invalidOrder
  return undefined
}
